/**
 * Pagina /superadmin/proyectos — Sprint 13 S13-25.
 *
 * Mantenedor de proyectos del barrio: iniciativas con presupuesto y
 * multiples gastos asociados ("Riego automatico plaza", "Pintura sede
 * social", "Camaras de seguridad"). Cada uno pasa por estados:
 * propuesto -> aprobado -> en_ejecucion -> cerrado (cancelado como
 * terminacion alternativa via soft delete).
 *
 * RBAC ruta: super_admin (sa). Backend acepta tambien tesorero.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  actualizarProyecto,
  cancelarProyecto,
  crearProyecto,
  listProyectos,
  type EstadoProyecto,
  type ProyectoCreate,
  type ProyectoRead,
} from '../../lib/api/proyectos';
import { Button } from '../../components/ui/Button';
import { ActionButton } from '../../components/ui/ActionButton';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';
import { InputFecha } from '../../components/ui/InputFecha';
import { formatCLP } from '../../lib/format';
import { hoy } from '../../lib/tiempo';

const ETIQUETA_ESTADO: Record<EstadoProyecto, string> = {
  propuesto: 'Propuesto',
  aprobado: 'Aprobado',
  en_ejecucion: 'En ejecución',
  cerrado: 'Cerrado',
  cancelado: 'Cancelado',
};

const TONE_ESTADO: Record<
  EstadoProyecto,
  'primary' | 'secondary' | 'muted' | 'success' | 'warning' | 'danger'
> = {
  propuesto: 'secondary',
  aprobado: 'warning',
  en_ejecucion: 'success',
  cerrado: 'muted',
  cancelado: 'danger',
};

const ESTADOS: EstadoProyecto[] = [
  'propuesto',
  'aprobado',
  'en_ejecucion',
  'cerrado',
  'cancelado',
];

interface ModalFormProps {
  inicial: ProyectoRead | null;
  onCerrar: () => void;
  onGuardado: () => void;
}

function ModalForm({ inicial, onCerrar, onGuardado }: ModalFormProps) {
  const esEdicion = inicial !== null;
  const fechaHoy = hoy();

  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(inicial?.descripcion ?? '');
  const [presupuesto, setPresupuesto] = useState(
    inicial ? String(inicial.presupuesto) : '',
  );
  const [inicio, setInicio] = useState(inicial?.inicio ?? fechaHoy);
  const [finEstimado, setFinEstimado] = useState(inicial?.fin_estimado ?? '');
  const [finReal, setFinReal] = useState(inicial?.fin_real ?? '');
  const [estado, setEstado] = useState<EstadoProyecto>(
    inicial?.estado ?? 'propuesto',
  );
  const [actaUrl, setActaUrl] = useState(inicial?.aprobado_acta_url ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const presupuestoNum = parseInt(presupuesto, 10);
  const valido =
    nombre.trim().length > 0 &&
    inicio !== '' &&
    !Number.isNaN(presupuestoNum) &&
    presupuestoNum >= 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: ProyectoCreate = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
        presupuesto: presupuestoNum,
        inicio,
        fin_estimado: finEstimado || null,
        fin_real: finReal || null,
        estado,
        aprobado_acta_url: actaUrl.trim() || null,
      };
      if (esEdicion) {
        await actualizarProyecto(inicial.id, payload);
      } else {
        await crearProyecto(payload);
      }
      onGuardado();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={esEdicion ? 'Editar proyecto' : 'Nuevo proyecto'}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-2xl w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            {esEdicion ? 'Editar proyecto' : 'Nuevo proyecto'}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Nombre <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Riego automático plaza, Pintura sede social…"
            maxLength={150}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Descripción</span>
          <textarea
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            rows={2}
            placeholder="Detalle del alcance, materiales, contrapartes, etc."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Presupuesto (CLP) <span className="text-state-danger">*</span>
            </span>
            <input
              type="number"
              value={presupuesto}
              onChange={(e) => setPresupuesto(e.target.value)}
              placeholder="500000"
              min={0}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            />
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Estado <span className="text-state-danger">*</span>
            </span>
            <select
              value={estado}
              onChange={(e) => setEstado(e.target.value as EstadoProyecto)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            >
              {ESTADOS.map((e) => (
                <option key={e} value={e} className="bg-bg-sunken text-text-primary">
                  {ETIQUETA_ESTADO[e]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Inicio <span className="text-state-danger">*</span>
            </span>
            <InputFecha
              value={inicio}
              onChange={setInicio}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              requerido
            />
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Fin estimado</span>
            <InputFecha
              value={finEstimado}
              onChange={setFinEstimado}
              min={inicio || undefined}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            />
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Fin real</span>
            <InputFecha
              value={finReal}
              onChange={setFinReal}
              min={inicio || undefined}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            />
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">URL del acta (opcional)</span>
          <input
            type="url"
            value={actaUrl}
            onChange={(e) => setActaUrl(e.target.value)}
            placeholder="https://…"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Crear proyecto'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function ProyectosPage() {
  const [items, setItems] = useState<ProyectoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<EstadoProyecto | 'todos'>(
    'todos',
  );
  const [search, setSearch] = useState('');
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [editando, setEditando] = useState<ProyectoRead | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [accionId, setAccionId] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listProyectos()
      .then((data) => {
        if (cancelado) return;
        setItems(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar proyectos`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (filtroEstado !== 'todos' && p.estado !== filtroEstado) return false;
      if (q && !p.nombre.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filtroEstado, search]);

  async function handleCancelar(p: ProyectoRead) {
    const ok = window.confirm(
      `¿Cancelar el proyecto "${p.nombre}"?\n\nEsto cambia su estado a "cancelado". Los gastos y compensaciones asociadas no se tocan. Si era un error, puedes volver a editarlo después y cambiarle el estado.`,
    );
    if (!ok) return;
    setAccionId(p.id);
    setErrorAccion(null);
    try {
      await cancelarProyecto(p.id);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorAccion(detail);
      } else {
        setErrorAccion('Error de red al cancelar');
      }
    } finally {
      setAccionId(null);
    }
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Iniciativas"
        title="Proyectos"
        description={
          <>
            Iniciativas con presupuesto definido y gastos asociados (ej.
            "Riego automático plaza", "Pintura sede social"). Pasan por
            estados <em>propuesto → aprobado → en ejecución → cerrado</em>.
            Cancelar es soft: el proyecto queda con estado "cancelado"
            pero su historia no se pierde.
          </>
        }
        action={
          <Button variant="primary" onClick={() => setMostrarCrear(true)}>
            + Nuevo proyecto
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre…"
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary w-72"
        />
        <select
          value={filtroEstado}
          onChange={(e) =>
            setFiltroEstado(e.target.value as EstadoProyecto | 'todos')
          }
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por estado"
        >
          <option value="todos">Todos los estados</option>
          {ESTADOS.map((e) => (
            <option key={e} value={e} className="bg-bg-sunken text-text-primary">
              {ETIQUETA_ESTADO[e]}
            </option>
          ))}
        </select>
        <span className="text-xs text-text-muted">
          {filtrados.length} de {items.length}
        </span>
      </div>

      {errorAccion && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {errorAccion}{' '}
          <button
            type="button"
            onClick={() => setErrorAccion(null)}
            className="underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {!loading && !error && filtrados.length === 0 && (
        <p className="text-sm text-text-muted">
          {items.length === 0
            ? 'No hay proyectos registrados. Usa "+ Nuevo proyecto" para crear el primero.'
            : 'No hay proyectos que coincidan con el filtro.'}
        </p>
      )}

      {!loading && !error && filtrados.length > 0 && (
        <Table>
          <THead>
            <Th>Nombre</Th>
            <Th>Estado</Th>
            <Th align="right">Presupuesto</Th>
            <Th>Inicio</Th>
            <Th>Fin</Th>
            <Th>Acta</Th>
            <Th align="right">Acciones</Th>
          </THead>
          <TBody>
            {filtrados.map((p) => (
              <Tr key={p.id} data-testid="fila-proyecto">
                <Td tone="primary">
                  <div className="font-medium">{p.nombre}</div>
                  {p.descripcion && (
                    <div className="text-xs text-text-muted truncate max-w-md">
                      {p.descripcion}
                    </div>
                  )}
                </Td>
                <Td tone={TONE_ESTADO[p.estado]} className="font-medium">
                  {ETIQUETA_ESTADO[p.estado]}
                </Td>
                <Td align="right" tone="primary">
                  {formatCLP(p.presupuesto)}
                </Td>
                <Td>{p.inicio}</Td>
                <Td>
                  {p.fin_real ? (
                    <span title="Fin real">{p.fin_real}</span>
                  ) : p.fin_estimado ? (
                    <span className="text-text-muted" title="Fin estimado">
                      ~{p.fin_estimado}
                    </span>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </Td>
                <Td className="truncate max-w-xs">
                  {p.aprobado_acta_url ? (
                    <a
                      href={p.aprobado_acta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-admin-red hover:underline"
                    >
                      Ver acta
                    </a>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <ActionButton
                      variant="edit"
                      onClick={() => setEditando(p)}
                      disabled={accionId === p.id}
                    >
                      Editar
                    </ActionButton>
                    {p.estado !== 'cancelado' && (
                      <ActionButton
                        variant="danger"
                        onClick={() => handleCancelar(p)}
                        disabled={accionId === p.id}
                      >
                        {accionId === p.id ? 'Cancelando…' : 'Cancelar'}
                      </ActionButton>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {mostrarCrear && (
        <ModalForm
          inicial={null}
          onCerrar={() => setMostrarCrear(false)}
          onGuardado={() => {
            setMostrarCrear(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {editando && (
        <ModalForm
          inicial={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
